import api from "@/api";

export default {
  namespaced: true,
  state: () => ({
    questions: [],
    currentQuestion: null,
    isLoading: false,
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 10,
      from: 0,
      to: 0,
    },
    filters: {
      search: "",
      subject_id: null,
      chapter_subject_id: null,
      question_type_id: null,
      question_level_id: null,
    },
  }),
  mutations: {
    SET_QUESTIONS(state, { data, meta }) {
      state.questions = data;
      state.pagination = {
        currentPage: meta.current_page,
        totalPages: meta.last_page,
        totalItems: meta.total,
        perPage: meta.per_page,
        from: meta.from,
        to: meta.to,
      };
    },
    SET_QUESTION(state, question) {
      state.currentQuestion = question;
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_PAGINATION(state, pagination) {
      state.pagination = pagination;
    },
    SET_FILTERS(state, filters) {
      state.filters = filters;
    },
    SET_PER_PAGE(state, perPage) {
      state.pagination.perPage = perPage;
    },
    ADD_QUESTION(state, question) {
      state.questions.unshift(question);
    },
    UPDATE_QUESTION(state, updatedQuestion) {
      const index = state.questions.findIndex(
        (q) => q.id === updatedQuestion.id
      );
      if (index !== -1) state.questions.splice(index, 1, updatedQuestion);
    },
    DELETE_QUESTION(state, questionId) {
      state.questions = state.questions.filter((q) => q.id !== questionId);
    },
  },
  actions: {
    async fetchQuestions({ commit, state }) {
      commit("SET_LOADING", true);
      try {
        const params = {
          page: state.pagination.currentPage,
          per_page: state.pagination.perPage,
          ...Object.fromEntries(
            Object.entries(state.filters).filter(
              ([, value]) => value !== null && value !== ""
            )
          ),
        };

        const response = await api.get("/questions", { params });
        commit("SET_QUESTIONS", {
          data: response.data.data,
          meta: response.data.meta,
        });
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchQuestion({ commit }, id) {
      commit("SET_LOADING", true);
      try {
        const response = await api.get(`/questions/${id}`);
        commit("SET_QUESTION", {
          ...response.data,
          answers: response.data.answers || [],
        });
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async createQuestion({ commit }, formData) {
      commit("SET_LOADING", true);
      try {
        const response = await api.post("/questions", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
        });
        commit("ADD_QUESTION", response.data.data);
        return response.data;
      } catch (error) {
        if (error.response?.status === 422) {
          throw error.response.data.errors;
        }
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async updateQuestion({ commit }, { id, questionData }) {
      commit("SET_LOADING", true);
      try {
        const response = await api.put(`/questions/${id}`, questionData);
        commit("UPDATE_QUESTION", response.data.data);
        return response.data;
      } catch (error) {
        if (error.response?.status === 422) {
          throw error;
        }
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async deleteQuestion({ commit }, questionId) {
      commit("SET_LOADING", true);
      try {
        await api.delete(`/questions/${questionId}`);
        commit("DELETE_QUESTION", questionId);
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async setFilters({ commit, dispatch, state }, filters) {
      commit("SET_FILTERS", filters);
      commit("SET_PAGINATION", {
        ...state.pagination,
        currentPage: 1,
      });
      await dispatch("fetchQuestions");
    },

    async setPerPage({ commit, dispatch, state }, perPage) {
      commit("SET_PER_PAGE", perPage);
      commit("SET_PAGINATION", {
        ...state.pagination,
        currentPage: 1,
      });
      await dispatch("fetchQuestions");
    },
  },
  getters: {
    questions: (state) => state.questions,
    question: (state) => state.currentQuestion,
    isLoading: (state) => state.isLoading,
    error: (state) => state.error,
    pagination: (state) => state.pagination,
    filters: (state) => state.filters,
  },
};
